import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { 
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ChevronDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

// Role Multi-Select Component
const RoleMultiSelect = ({ userId, currentRoles, onRolesChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState(currentRoles || []);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedRoles(currentRoles || []);
  }, [currentRoles]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const allRoles = [
    { value: 'player', label: 'Player' },
    { value: 'parent', label: 'Parent' },
    { value: 'coach', label: 'Coach' },
    { value: 'assistant_coach', label: 'Assistant Coach' },
    { value: 'admin', label: 'Admin' },
    { value: 'org_admin', label: 'Org Admin' },
    { value: 'app_manager', label: 'App Manager' },
  ];

  const toggleRole = (roleValue) => {
    const newRoles = selectedRoles.includes(roleValue)
      ? selectedRoles.filter(r => r !== roleValue)
      : [...selectedRoles, roleValue];
    setSelectedRoles(newRoles);
  };

  const handleSave = () => {
    if (selectedRoles.length === 0) {
      toast.error('At least one role must be selected');
      return;
    }
    onRolesChange(selectedRoles);
    setIsOpen(false);
  };

  const getRoleLabel = (roleValue) => {
    return allRoles.find(r => r.value === roleValue)?.label || roleValue;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[150px] text-left flex items-center justify-between"
      >
        <span className="truncate">
          {selectedRoles.length > 0 
            ? `${selectedRoles.length} role${selectedRoles.length > 1 ? 's' : ''}`
            : 'Select roles'}
        </span>
        <ChevronDownIcon className="h-4 w-4 ml-2 flex-shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50">
          <div className="p-2">
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2 px-2">
              Select Roles
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {allRoles.map((role) => (
                <label
                  key={role.value}
                  className="flex items-center px-2 py-1.5 hover:bg-gray-50 cursor-pointer rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.value)}
                    onChange={() => toggleRole(role.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{role.label}</span>
                  {selectedRoles.includes(role.value) && (
                    <CheckIcon className="h-4 w-4 text-blue-600 ml-auto" />
                  )}
                </label>
              ))}
            </div>
            <div className="flex justify-end space-x-2 mt-3 pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setSelectedRoles(currentRoles || []);
                  setIsOpen(false);
                }}
                className="px-3 py-1 text-xs text-gray-700 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const UserManagement = ({ organizationId = null, embedded = false }) => {
  const { user } = useAuth();
  const [confirmState, setConfirmState] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    roleBreakdown: {},
    recentRegistrations: 0
  });
  const [showGuestsOnly, setShowGuestsOnly] = useState(false);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const orgParam = organizationId ? `?organization_id=${organizationId}` : '';

      // Fetch users
      const usersResponse = await fetch(`/api/users${orgParam}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData);
      }

      // Fetch user stats
      const statsResponse = await fetch(`/api/users/stats${orgParam}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim() || !selectedUser) return;

    try {
      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          content: messageContent,
          recipient_id: selectedUser.id,
          type: 'direct'
        })
      });

      if (response.ok) {
        setMessageContent('');
        setShowMessageModal(false);
        setSelectedUser(null);
        toast.success('Message sent successfully!');
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleRoleChange = async (userId, selectedRoles) => {
    try {
      const response = await fetch(`/api/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ roles: selectedRoles })
      });

      if (response.ok) {
        const result = await response.json();
        setUsers(prev => prev.map(user => 
          user.id === userId ? { 
            ...user, 
            role: result.user.role,
            roles: result.user.roles 
          } : user
        ));
        toast.success('User roles updated!');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update user roles');
      }
    } catch (error) {
      console.error('Error updating user roles:', error);
      toast.error('Failed to update user roles');
    }
  };

  const handleDeactivateUser = (userId) => {
    setConfirmState({
      title: 'Deactivate User',
      message: 'Are you sure you want to deactivate this user?',
      isDangerous: true,
      confirmLabel: 'Deactivate',
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/users/${userId}/deactivate`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          });
          if (response.ok) {
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'inactive' } : u));
            toast.success('User deactivated.');
          } else {
            toast.error('Failed to deactivate user');
          }
        } catch (error) {
          console.error('Error deactivating user:', error);
          toast.error('Failed to deactivate user');
        }
      },
    });
  };

  const filteredUsers = users.filter(user => {
    const userRoles = user.roles && Array.isArray(user.roles) && user.roles.length > 0 
      ? user.roles 
      : [user.role];
    const roleString = userRoles.join(' ').toLowerCase();
    
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      roleString.includes(searchTerm.toLowerCase());
    
    if (showGuestsOnly) {
      return matchesSearch && user.isGuest;
    }
    
    return matchesSearch;
  });

  const getRoleColor = (role) => {
    const colors = {
      player: 'bg-blue-100 text-blue-800',
      parent: 'bg-green-100 text-green-800',
      coach: 'bg-purple-100 text-purple-800',
      assistant_coach: 'bg-indigo-100 text-indigo-800',
      admin: 'bg-red-100 text-red-800',
      org_admin: 'bg-orange-100 text-orange-800',
      app_manager: 'bg-yellow-100 text-yellow-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      online: 'bg-green-100 text-green-800',
      away: 'bg-yellow-100 text-yellow-800',
      offline: 'bg-gray-100 text-gray-800',
      inactive: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!embedded && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <UserGroupIcon className="h-8 w-8 text-blue-600 mr-3" />
                User Management
              </h1>
              <p className="text-gray-600 mt-1">
                Manage users, roles, and permissions
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 font-bold">A</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Coaches</p>
              <p className="text-2xl font-bold text-gray-900">
                {(stats.roleBreakdown.coach || 0) + (stats.roleBreakdown.assistant_coach || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <CalendarIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Recent</p>
              <p className="text-2xl font-bold text-gray-900">{stats.recentRegistrations}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowGuestsOnly(!showGuestsOnly)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              showGuestsOnly
                ? 'bg-blue-100 text-blue-700 border-blue-300'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {showGuestsOnly ? '✓ Showing Guests Only' : 'Show Guests Only'}
          </button>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {filteredUsers.length} {filteredUsers.length === 1 ? 'User' : 'Users'}
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredUsers.map((user) => (
            <div key={user.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-gray-200 rounded-full flex items-center justify-center">
                      {user.profilePicture ? (
                        <img
                          src={user.profilePicture}
                          alt={user.name}
                          className="h-full w-full rounded-full object-cover"
                        />
                      ) : (
                        <UserCircleIcon className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {user.name}
                      </h3>
                      {/* Display all roles */}
                      {(user.roles && Array.isArray(user.roles) && user.roles.length > 0 ? user.roles : [user.role]).map((role, idx) => (
                        <span key={idx} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(role)}`}>
                          {role.replace('_', ' ')}
                        </span>
                      ))}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status || 'offline')}`}>
                        {user.status || 'offline'}
                      </span>
                      {user.isGuest && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Guest
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <EnvelopeIcon className="h-4 w-4 mr-1" />
                        {user.email}
                      </span>
                      {user.phone && (
                        <span className="flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-1" />
                          {user.phone}
                        </span>
                      )}
                      <span className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        Joined {formatDate(user.createdAt)}
                      </span>
                    </div>
                    {user.teamName && (
                      <p className="mt-1 text-sm text-gray-600">Team: {user.teamName}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      setShowMessageModal(true);
                    }}
                    className="p-2 text-gray-400 hover:text-blue-600"
                    title="Send Message"
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5" />
                  </button>
                  
                  <RoleMultiSelect
                    userId={user.id}
                    currentRoles={user.roles && Array.isArray(user.roles) && user.roles.length > 0 ? user.roles : [user.role]}
                    onRolesChange={(selectedRoles) => handleRoleChange(user.id, selectedRoles)}
                  />

                  {user.status !== 'inactive' && (
                    <button
                      onClick={() => handleDeactivateUser(user.id)}
                      className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Send Message to {selectedUser.name}
              </h3>
              <textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Type your message..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowMessageModal(false);
                    setSelectedUser(null);
                    setMessageContent('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

export default UserManagement;